/**
 * @fileoverview O checklist do Dashboard (etapa 5 dos planos, parte 4c): o que
 * ainda falta para a conta atender (o WhatsApp, a equipe, a IA e o horário),
 * detectado sozinho pelo servidor. Só nas Empresas que nasceram pelo cadastro,
 * para quem configura a conta (company.manage); cada pessoa pode dispensá-lo,
 * e ele some com tudo pronto. Cada item leva à tela onde se faz, quando a
 * pessoa pode fazer (as permissões de cada tela, com a regra de hoje).
 */

import React, { useContext, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { Check, X } from "lucide-react";

import { allows } from "../Can";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePermission, { isAdminUser } from "../../hooks/usePermission";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { isInternalOnly } from "../../utils/accessScope";

const t = (key, options) => i18n.t(`setupChecklist.${key}`, options);

const ITEMS = [
  { key: "whatsapp", to: "/connections", permission: "connections.manage", legacy: (user) => allows(user, "connections-page:addConnection") },
  { key: "team", to: "/users", permission: "team.manage", legacy: isAdminUser },
  { key: "ai", to: "/assistente-ia", permission: "ai_agents.manage", legacy: (user) => allows(user, "drawer-admin-items:view") },
  { key: "hours", to: "/settings/schedules", permission: "schedules.manage", legacy: (user) => isAdminUser(user) && !isInternalOnly(user) },
];

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const text = dark ? "#FAFAFA" : "#09090B";
  const muted = dark ? "#A1A1AA" : "#52525B";
  const line = dark ? "rgba(255,255,255,0.08)" : "#E4E4E7";
  const focus = dark ? "rgba(91,124,255,0.6)" : "rgba(61,92,253,0.55)";
  return {
    card: {
      margin: "0 0 16px", padding: "16px 18px", borderRadius: 14, border: `1px solid ${line}`,
      background: dark ? "#111113" : "#FFFFFF", color: text,
    },
    head: { display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 12 },
    // O título e o progresso: na largura do celular, o progresso desce para baixo do título.
    heading: { flex: 1, minWidth: 0, display: "flex", alignItems: "baseline", flexWrap: "wrap", columnGap: 12, rowGap: 2, paddingTop: 4 },
    title: { margin: 0, fontSize: 15, fontWeight: 700 },
    progress: { fontSize: 12.5, color: muted, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
    dismiss: {
      marginLeft: "auto", width: 30, height: 30, padding: 0, border: "none", borderRadius: 8, background: "transparent", color: muted,
      display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
      "&:hover": { background: dark ? "rgba(255,255,255,0.06)" : "#F4F4F5", color: text },
      "&:focus-visible": { outline: `1px solid ${focus}`, outlineOffset: 1 },
    },
    list: {
      listStyle: "none", margin: 0, padding: 0, display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 10,
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0, 1fr)" },
    },
    item: { display: "flex", gap: 10, padding: "12px", borderRadius: 10, border: `1px solid ${line}`, minWidth: 0 },
    mark: {
      flexShrink: 0, width: 20, height: 20, marginTop: 1, borderRadius: "50%", border: `1px solid ${dark ? "rgba(255,255,255,0.22)" : "#D4D4D8"}`,
      display: "inline-flex", alignItems: "center", justifyContent: "center", boxSizing: "border-box",
    },
    markDone: { background: text, borderColor: text, color: dark ? "#0A0A0A" : "#FFFFFF" },
    body: { minWidth: 0 },
    itemTitle: { fontSize: 13.5, fontWeight: 600, lineHeight: 1.35 },
    itemDone: { color: muted, fontWeight: 500 },
    itemText: { margin: "2px 0 0", fontSize: 12.5, color: muted, lineHeight: 1.45 },
    // Lido pelo leitor de tela (o check do item já diz na tela).
    srOnly: { position: "absolute", width: 1, height: 1, margin: -1, padding: 0, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", border: 0 },
    action: {
      display: "inline-block", marginTop: 6, fontSize: 12.5, fontWeight: 600, color: `${text} !important`, textDecoration: "underline",
      textUnderlineOffset: "3px", borderRadius: 4,
      "&:focus-visible": { outline: `1px solid ${focus}`, outlineOffset: 2 },
    },
  };
});

const SetupChecklist = () => {
  const classes = useStyles();
  const { user, impersonation } = useContext(AuthContext);
  const { can } = usePermission();
  const visible = can("company.manage", isAdminUser(user));
  // No "Acessar como" de uma pessoa, a dispensa seria dela: o botão não aparece (o servidor também recusa).
  const canDismiss = impersonation?.mode !== "user";
  const [checklist, setChecklist] = useState(null);

  useEffect(() => {
    if (!visible) return undefined;
    let alive = true;
    api
      .get("/onboarding/checklist")
      .then(({ data }) => alive && setChecklist(data))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [visible]);

  if (!visible || !checklist?.show || !checklist.items) return null;

  // Dispensar some na hora; se o servidor não gravar, o checklist volta na próxima visita.
  const dismiss = () => {
    setChecklist(null);
    api.post("/onboarding/checklist/dismiss").catch(() => undefined);
  };

  const done = ITEMS.filter((item) => checklist.items[item.key]).length;

  return (
    <section className={classes.card} aria-labelledby="setup-checklist-title">
      <div className={classes.head}>
        <div className={classes.heading}>
          <h2 id="setup-checklist-title" className={classes.title}>{t("title")}</h2>
          <span className={classes.progress}>{t("progress", { done, total: ITEMS.length })}</span>
        </div>
        {canDismiss && (
          <button type="button" className={classes.dismiss} onClick={dismiss} aria-label={t("dismiss")} title={t("dismissHint")}>
            <X size={16} />
          </button>
        )}
      </div>
      <ul className={classes.list}>
        {ITEMS.map((item) => {
          const ready = !!checklist.items[item.key];
          const canDo = can(item.permission, item.legacy(user));
          return (
            <li key={item.key} className={classes.item}>
              <span className={`${classes.mark}${ready ? ` ${classes.markDone}` : ""}`} aria-hidden="true">
                {ready && <Check size={12} strokeWidth={3} />}
              </span>
              <div className={classes.body}>
                <div className={`${classes.itemTitle}${ready ? ` ${classes.itemDone}` : ""}`}>
                  {t(`items.${item.key}.title`)}
                  {ready && <span className={classes.srOnly}>{` (${t("done")})`}</span>}
                </div>
                <p className={classes.itemText}>{t(`items.${item.key}.text`)}</p>
                {!ready && canDo && (
                  <RouterLink to={item.to} className={classes.action}>{t(`items.${item.key}.action`)}</RouterLink>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default SetupChecklist;
